import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alerts,
  Button,
  Container,
  FormActions,
  FormNotice,
  FormSection,
  FormSummary,
  FormSummaryHeading,
  FormSummaryMeta,
  Modal,
  PageContent,
  StatusBadge,
} from 'components';
import { checkPermissions, globalPropTypes } from 'helpers';
import { formatDateTime } from 'helpers/date';
import { useAccessGuard } from 'hooks';
import ReportBody from 'containers/yc/Reports/ReportForm/ReportBody';
import StatusHistory from 'containers/yc/Reports/ReportForm/StatusHistory';
import buildFormState from 'containers/yc/Reports/ReportForm/reportState';
import {
  loadIpmaYcReportCreator,
  decideIpmaYcReportCreator,
  removeIpmaYcReportCreator,
} from 'store/actionsCreators';
import { SummaryList, ReasonInput, ReasonLabel, ReasonHelp, ReasonError } from './Review.styled';

/**
 * The reason of a decision.
 *
 * Uncontrolled and cut off from the state of the screen: the modal rebuilds its
 * content whenever the screen re-renders, so a controlled field would lose the
 * focus after every keystroke. The value is handed over through a ref instead.
 */
const ReasonField = ({ label, description, placeholder, error, onChange }) => (
  <>
    <ReasonLabel>{label}</ReasonLabel>
    {description ? <ReasonHelp>{description}</ReasonHelp> : null}
    <ReasonInput
      rows={4}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
    {error ? <ReasonError>{error}</ReasonError> : null}
  </>
);

ReasonField.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

ReasonField.defaultProps = {
  description: null,
  error: null,
};

/**
 * One Young Crew yearly report, read by the YC Management Board.
 *
 * The four groups are rendered by the very same component the Young Crew fills
 * in, with every input disabled - what is reviewed is what was sent, not a
 * second rendering of it. Underneath, the board answers once: accepted, or
 * rejected with the reason the Young Crew reworks the report from.
 */
const Review = ({ data, errors, userPermissions, loadReport, decideReport, clearReport }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();

  const [form, setForm] = useState(null);
  // null while no decision is being confirmed, true / false once accept or
  // reject was picked and the modal is asking for the reason.
  const [decision, setDecision] = useState(null);
  const reason = useRef('');

  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    checkPermissions({ roles: ['ipma_yc_report_view'] }, userPermissions || []),
    '/ipma',
    'page.ipma.ycReports.accessDenied',
  );

  useEffect(() => {
    if (!allowed) return undefined;

    loadReport({ id: Number(id) });

    return () => clearReport();
  }, [id, allowed]);

  useEffect(() => {
    setForm(buildFormState(data));
  }, [data]);

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  if (!data || !form) {
    return <PageContent />;
  }

  const { report, youngCrew, author } = data;
  const backPath = '/ipma/reports/yc';

  const openDecision = (accept) => {
    reason.current = '';
    setDecision(accept);
  };

  const confirmDecision = () => {
    decideReport({ id: report.id, accept: decision, reason: reason.current }, history);
    setDecision(null);
  };

  const renderDecision = () => {
    if (!data.decidable) {
      return (
        <FormActions>
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => history.push(backPath)}
          >
            {t('page.ipma.ycReports.back')}
          </Button>
        </FormActions>
      );
    }

    return (
      <FormSection
        title={t('page.ipma.ycReports.decisionSection')}
        description={t('page.ipma.ycReports.decisionSectionHelp')}
      >
        <FormActions>
          <Button type="button" style={{ width: 'auto' }} onClick={() => openDecision(true)}>
            {t('page.ipma.ycReports.accept')}
          </Button>
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => openDecision(false)}
          >
            {t('page.ipma.ycReports.reject')}
          </Button>
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => history.push(backPath)}
          >
            {t('page.ipma.ycReports.back')}
          </Button>
        </FormActions>
      </FormSection>
    );
  };

  return (
    <PageContent>
      <Container>
        <Alerts type="ipma" />

        <FormSummary>
          <div>
            <FormSummaryHeading>
              {t('page.ipma.ycReports.reviewHeading', {
                youngCrew: youngCrew ? youngCrew.name || youngCrew.initials : '',
                year: data.year,
              })}
            </FormSummaryHeading>
            <FormSummaryMeta>
              {t('page.ipma.ycReports.sentOnValue', {
                date: report.submittedAt ? formatDateTime(report.submittedAt) : '-',
              })}
            </FormSummaryMeta>
          </div>
          <StatusBadge status={report.status}>{report.statusLabel}</StatusBadge>
        </FormSummary>

        {data.rejectionReason ? (
          <FormNotice type="error">
            {t('page.yc.reports.form.reason', { reason: data.rejectionReason })}
          </FormNotice>
        ) : null}

        <FormSection title={t('page.ipma.ycReports.senderSection')}>
          <SummaryList>
            <dt>{t('page.ipma.ycReports.youngCrew')}</dt>
            <dd>{youngCrew ? youngCrew.name : '-'}</dd>
            <dt>{t('page.ipma.ycReports.country')}</dt>
            <dd>{youngCrew && youngCrew.country ? youngCrew.country : '-'}</dd>
            <dt>{t('page.ipma.ycReports.sentBy')}</dt>
            <dd>{author ? `${author.name} (${author.email})` : '-'}</dd>
            <dt>{t('page.ipma.ycReports.year')}</dt>
            <dd>{data.year}</dd>
          </SummaryList>
        </FormSection>

        <ReportBody data={data} form={form} readOnly />

        {renderDecision()}

        <StatusHistory items={data.statusHistory} />
      </Container>

      <Modal
        heading={
          decision ? t('page.ipma.ycReports.confirmAccept') : t('page.ipma.ycReports.confirmReject')
        }
        description={
          <ReasonField
            label={
              decision
                ? t('page.ipma.ycReports.reasonLabel')
                : t('page.ipma.ycReports.reasonRejectLabel')
            }
            description={decision ? undefined : t('page.ipma.ycReports.reasonRejectHelp')}
            placeholder={t('page.ipma.ycReports.reasonPlaceholder')}
            error={errors.reason}
            onChange={(value) => {
              reason.current = value;
            }}
          />
        }
        isOpen={decision !== null}
        confirm={confirmDecision}
        cancel={() => setDecision(null)}
        onRequestClose={() => setDecision(null)}
      />
    </PageContent>
  );
};

Review.propTypes = {
  data: PropTypes.shape({
    year: PropTypes.number,
    report: PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.string,
      statusLabel: PropTypes.string,
      submittedAt: PropTypes.string,
    }),
    youngCrew: PropTypes.shape({
      name: PropTypes.string,
      initials: PropTypes.string,
      country: PropTypes.string,
    }),
    author: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    }),
    decidable: PropTypes.bool,
    rejectionReason: PropTypes.string,
    statusHistory: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  errors: PropTypes.objectOf(PropTypes.string),
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  loadReport: PropTypes.func.isRequired,
  decideReport: PropTypes.func.isRequired,
  clearReport: PropTypes.func.isRequired,
};

Review.defaultProps = {
  data: null,
  errors: {},
};

const mapStateToProps = ({ ipma, user }) => ({
  data: ipma.ycReports.review.data,
  errors: ipma.ycReports.review.errors,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadReport: (payload) => dispatch(loadIpmaYcReportCreator(payload)),
  decideReport: (payload, history) => dispatch(decideIpmaYcReportCreator(payload, history)),
  clearReport: () => dispatch(removeIpmaYcReportCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Review);
